/**
 * RAOC-22 PR-2 — Copied from
 * `@atlassian/search-page/src/controllers/config/utils/third-party-products.ts`.
 *
 * Internal helper for `useRovoConfig`. Not exported via a package subpath.
 *
 * Brings the following feature gates with it (all registered in this package's
 * `platform-feature-flags` map; cleanup tracked in their respective FFCLEANUP
 * tickets — see RAOC-22 plan FG inventory):
 *   - `rovo-search-dynamic-forge-config` (FFCLEANUP-94074)
 *   - `ai_3p_slack_connector_status_transition_enabled` (no FFCLEANUP yet)
 *   - `ai3p_webcrawler_multi_domain_support` (FFCLEANUP-96797)
 */

import { fg } from '@atlaskit/platform-feature-flags';
import { type TSearch3pConfigAPIResponse } from '@atlassian/search-client/fetch-search-config';

import {
	type ConnectorSource,
	FEDERATED_CONNECTOR_SOURCE,
	type ForgeConnectorProductKey,
	FULL_CONNECTOR_SOURCE,
	isForgeConnectorProductKey,
	LITE_CONNECTOR_SOURCE,
	SMARTLINKS_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../common/types/schemas/types';
import { ThirdPartyConfigsSync } from '../../common/ui/configs/sync';
import { type ProductKeys3P } from '../../common/ui/configs/types';
import { isUsingRovoConfigStoreExperiment } from '../../common/utils/experiments';
import { allProductKeys } from '../../constants/product-keys';

import { mapForgeConnectorToThirdPartyConfig, type OnForgeConnectorTypeMappingFn } from './forge';

type ConnectorSourceMap = Map<string, Set<ConnectorSource>>;

const FEDERATED_NO_OAUTH_PRODUCT_KEYS = new Set<ProductKeys3P>(['servicenow', 'zendesk']);

/**
 * Returns the dictionary of API connector `type` strings → product-key strings
 * used by `map3pResourcesToProductKeys`. Mirrors the search-page version of
 * `getThirdPartyTypes`, but reads from `ThirdPartyConfigsSync` (this package's
 * synchronous third-party config map) instead of the search-page async map.
 */
export const getThirdPartyTypes = (): Record<string, string> => {
	// old sharepoint product key is put here (kept for backwards compatibility), as the new one is in the config
	const oldThirdPartyTypes: Record<string, string> = {
		'ai-3p-connector:sharepoint-domain': `sharepoint`,
		'ai-3p-connector:google-workspace': `drive`,
	};

	const thirdPartyTypes: Record<string, string> = {
		...oldThirdPartyTypes,
		'lite-connector-gdrive': `drive`,
		'smartlinks-connector-gdrive': `drive`,
		'smartlinks-connector-gdrive-rsl': `drive`,
		'smartlinks-connector-salesforce': `salesforce`,
		'smartlinks-connector-airtable': `airtable`,
		'smartlinks-connector-figma': `figma`,
		'smartlinks-connector-gitlab': `gitlab`,
		'smartlinks-connector-miro': `miro`,
		'smartlinks-connector-asana': `asana`,
		'smartlinks-connector-smartsheet': `smartsheet`,
		'smartlinks-connector-dropbox': `dropbox`,
		'smartlinks-connector-box': `box`,
		'smartlinks-connector-lucid': `lucid`,
		'smartlinks-connector-hubspot': `hubspot`,
		'smartlinks-connector-docusign': `docusign`,
		'smartlinks-connector-slack': `slack`,
		'smartlinks-connector-teams': `teams`,
		'smartlinks-connector-azure-devops': `azure-devops`,
		'smartlinks-connector-notion': `notion`,
		'smartlinks-connector-onedrive': `onedrive`,
		'smartlinks-connector-sharepoint': `sharepoint`,
	};

	ThirdPartyConfigsSync.forEach((config) => {
		if (config.resourceType) {
			thirdPartyTypes[config.resourceType] = config.id;
		}
	});

	return thirdPartyTypes;
};

export const map3pResourcesToProductKeys = (
	resources: TSearch3pConfigAPIResponse,
	onForgeConnectorTypeMapping?: OnForgeConnectorTypeMappingFn,
): {
	productKeys: string[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
} => {
	const THIRD_PARTY_TYPES: Record<string, string> = getThirdPartyTypes();

	const connectorSourceMap: ConnectorSourceMap = new Map();

	let productKeys: (ProductKeys3P | ForgeConnectorProductKey)[] = [];
	let thirdPartyConfigsWithAPIResponse: ThirdPartyConfigsBootstrap = {};

	resources.forEach((resource) => {
		if (isForgeConnectorProductKey(resource.type) && fg('rovo-search-dynamic-forge-config')) {
			const config = mapForgeConnectorToThirdPartyConfig(resource, onForgeConnectorTypeMapping);
			if (config) {
				const productKey = resource.type as ForgeConnectorProductKey;
				productKeys.push(productKey);
				thirdPartyConfigsWithAPIResponse[productKey] = config;
			}
			return;
		}

		const productKey = (THIRD_PARTY_TYPES[resource.type] || '') as ProductKeys3P;
		if (!productKey) {
			return;
		}

		const { providerId, datasourceId } = resource;
		// NOTE: `productKey` is pushed unconditionally, BEFORE the `if (staticConfig)` guard
		// below. This means a connector type present in `THIRD_PARTY_TYPES` but absent from
		// `ThirdPartyConfigsSync` will appear in the returned `productKeys` without a
		// corresponding entry in `thirdPartyConfigs`. Downstream consumers must therefore
		// treat `thirdPartyConfigs[productKey]` as possibly-undefined for keys that exist in
		// `productKeys`. This is the verbatim search-page behaviour — changing the ordering
		// here would alter the analytics/contract surface and break the search-page
		// equivalence test. RAOC-22 follow-up: reconcile in a behaviour-fix PR after PR-4.
		productKeys.push(productKey);
		let config = {} as ThirdPartyConfigsWithAPIResponse;
		const staticConfig = ThirdPartyConfigsSync.get(productKey);
		if (staticConfig) {
			const isSlackConnectorSyncTypeEnabled =
				productKey === 'slack' && fg('ai_3p_slack_connector_status_transition_enabled');
			const isFederatedNoOAuthConnector =
				FEDERATED_NO_OAUTH_PRODUCT_KEYS.has(productKey) &&
				resource.connectorSyncType === FEDERATED_CONNECTOR_SOURCE &&
				fg('rovo_fps_hide_federated_servicenow_zendesk_auth');

			let sourceType: ConnectorSource;

			if (resource.type.startsWith(SMARTLINKS_CONNECTOR_SOURCE)) {
				sourceType = SMARTLINKS_CONNECTOR_SOURCE;
			} else if (resource.type.startsWith(LITE_CONNECTOR_SOURCE)) {
				sourceType = LITE_CONNECTOR_SOURCE;
			} else if (isFederatedNoOAuthConnector) {
				sourceType = FEDERATED_CONNECTOR_SOURCE;
			} else if (isSlackConnectorSyncTypeEnabled) {
				sourceType =
					resource.connectorSyncType === 'federated'
						? FEDERATED_CONNECTOR_SOURCE
						: FULL_CONNECTOR_SOURCE;
			} else if (staticConfig.isFederated) {
				sourceType = FEDERATED_CONNECTOR_SOURCE;
			} else {
				sourceType = FULL_CONNECTOR_SOURCE;
			}

			connectorSourceMap.set(
				providerId,
				(connectorSourceMap.get(providerId) || new Set()).add(sourceType),
			);

			const existingConfig = thirdPartyConfigsWithAPIResponse[productKey];
			const isExistingConfigFullConnectorSource =
				existingConfig?.connectorSources?.includes(FULL_CONNECTOR_SOURCE) || false;

			const outboundAuthUrl = isFederatedNoOAuthConnector
				? ''
				: isExistingConfigFullConnectorSource && existingConfig?.outboundAuthUrl
					? existingConfig.outboundAuthUrl
					: resource.outboundAuthUrl;

			const userNeedsOAuth = isFederatedNoOAuthConnector
				? false
				: isExistingConfigFullConnectorSource && existingConfig
					? existingConfig.userNeedsOAuth
					: !resource.isUserOAuthed;

			// The static config's `resourceType` is the *full-connector* key
			// (e.g. `box-connector`), which is correct for full/federated/lite
			// connections. But a SmartLinks connection arrives with a distinct
			// `resource.type` (e.g. `smartlinks-connector-box`) that the chat
			// layer must be able to disambiguate so it can remap to the
			// SmartLinks-specific source (e.g. `box_sl`). Inheriting only the
			// static key collapses both variants onto the full-connector source,
			// so for a *pure* SmartLinks connection we preserve the real
			// `resource.type`. See `resolveChatSource` / `SOURCE_BY_RESOURCE_TYPE`.
			//
			// Full always wins: a single product key holds one merged config, so
			// when a full connector also exists for this product we must keep the
			// full-connector `resourceType` (→ chat source `box`, not `box_sl`),
			// regardless of the order the endpoint returns the two variants in.
			// `isExistingConfigFullConnectorSource` covers "full processed first";
			// a non-SmartLinks current resource already yields the static key, so
			// the only way to reach the SmartLinks key is a SmartLinks resource
			// with no full sibling already recorded.
			//
			// We key the decision off the already-computed `sourceType` so that the
			// SmartLinks detection here cannot drift from the `sourceType`
			// classification above (both use `SMARTLINKS_CONNECTOR_SOURCE`).
			//
			// Gated behind `rovo_cas_use_rovo_config`: `map3pResourcesToProductKeys`
			// also feeds the (currently un-gated) `getThirdPartyConfigs` /
			// `parseCombinedConfig` path, so we only change the emitted
			// `resourceType` when the chat-side (CAS) consumer is enabled. When the
			// gate is off we keep the legacy static full-connector key.
			const resourceType =
				sourceType === SMARTLINKS_CONNECTOR_SOURCE &&
				!isExistingConfigFullConnectorSource &&
				isUsingRovoConfigStoreExperiment()
					? resource.type
					: staticConfig.resourceType;

			config = {
				...staticConfig,
				resourceType,
				outboundAuthUrl,
				userNeedsOAuth,
				providerId,
				datasourceId,
				connectionId: resource.connectionId,
				workspaceName: resource.workspaceName,
				workspaceUrl: resource.workspaceUrl,
				connectorSources: Array.from(connectorSourceMap.get(providerId) || []),
				impersonationEnabled: resource.isImpersonationAuthEnabled,
				connectorCreatedAt: resource.connectorCreatedAt ?? undefined,
				connectedAt: resource.connectedAt ?? undefined,
			};

			const datasourceList = [];
			const { datasources: _datasources, ...configWithoutDatasources } = config;
			configWithoutDatasources.datasourceId = resource.datasourceId;
			configWithoutDatasources.connectionId = resource.connectionId;
			const datasource = {
				...configWithoutDatasources,
				...resource,
				outboundAuthUrl: isFederatedNoOAuthConnector ? '' : resource.outboundAuthUrl,
				userNeedsOAuth: isFederatedNoOAuthConnector ? false : !resource.isUserOAuthed,
				integrationAri: resource.integrationId || configWithoutDatasources.integrationAri,
			};
			datasourceList.push(datasource);
			const extraDatasources = thirdPartyConfigsWithAPIResponse[productKey]?.datasources || [];
			const cleanedExtraDatasources = extraDatasources.map(
				// Renamed to avoid shadowing the outer `_datasources` / `configWithoutDatasources`
				// destructuring above (introduced when removing the eslint-disable in the same
				// review round).
				({ datasources: _d, ...cleanedEntry }) => cleanedEntry,
			);
			datasourceList.push(...cleanedExtraDatasources);
			config.datasources = datasourceList as ThirdPartyConfigsWithAPIResponse[];
			thirdPartyConfigsWithAPIResponse[productKey] = config;
		}
	});

	// Filter product keys to only include valid ones.
	const filteredProductKeys = productKeys.filter((productKey) => {
		if (isForgeConnectorProductKey(productKey)) {
			return true;
		}
		return allProductKeys.includes(productKey as ProductKeys3P);
	});

	const deduplicatedProductKeys = [...new Set(filteredProductKeys)];

	return {
		productKeys: deduplicatedProductKeys,
		thirdPartyConfigs: thirdPartyConfigsWithAPIResponse,
	};
};
